import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "@/lib/motion";
import {
    Star, Minus, Plus, Heart, ShoppingBag, Truck, Store, Package, Calendar, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Reveal, Stagger } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FULFILL = [
    { key: "pickup", label: "Pickup", icon: Store, note: "Ready in 2 hours" },
    { key: "delivery", label: "Local Delivery", icon: Truck, note: "Same-day nearby" },
    { key: "shipping", label: "Shipping", icon: Package, note: "2–3 day chilled" },
];

const BASES = ["Graham Cracker", "Golden Oreo", "Biscoff Cookie", "Twix", "No thanks"];
const TOPPINGS = ["Graham Cracker Crumble", "Golden Oreo Crumble", "Biscoff Cookie Crumble", "Twix Crumble", "No thanks"];
const DRIZZLES = ["Caramel Drizzle", "Honey Drizzle", "Chocolate Drizzle", "Butterscotch", "No thanks"];
const FRUITS = ["Bananas", "Peaches", "Strawberries", "No thanks"];

function fulfillmentDate(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

function OptionGroup({ label, options, value, onChange, testPrefix }) {
    const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    return (
        <div className="mt-5">
            <p className="mb-2 text-sm font-semibold text-roast">{label}</p>
            <div className="flex flex-wrap gap-2">
                {options.map((opt) => (
                    <button
                        key={opt}
                        onClick={() => onChange(opt)}
                        data-testid={`${testPrefix}-${slug(opt)}`}
                        className={`rounded-full border px-4 py-2 text-sm font-medium transition-all active:scale-95 ${
                            value === opt
                                ? "border-caramel bg-banana/25 text-roast"
                                : "border-caramel/15 bg-white text-roast/80 hover:border-caramel/40"
                        }`}
                    >
                        {opt}
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function ProductDetail() {
    const { slug } = useParams();
    const { addItem } = useCart();
    const { user } = useAuth();

    const { data: product, isLoading } = useQuery({
        queryKey: ["product", slug],
        queryFn: async () => (await api.get(`/products/${slug}`)).data,
    });
    const { data: config } = useQuery({
        queryKey: ["config"],
        queryFn: async () => (await api.get("/config")).data,
    });

    const [activeImg, setActiveImg] = useState(0);
    const [sizeKey, setSizeKey] = useState("cup12");
    const [qty, setQty] = useState(1);
    const [zoom, setZoom] = useState(null);
    const [fav, setFav] = useState(false);
    const [base, setBase] = useState(BASES[0]);
    const [topping, setTopping] = useState(TOPPINGS[0]);
    const [drizzle, setDrizzle] = useState(DRIZZLES[0]);
    const [fruit, setFruit] = useState(FRUITS[0]);
    const [fulfillment, setFulfillment] = useState(() => {
        try { return localStorage.getItem("sp_fulfillment") || "pickup"; } catch { return "pickup"; }
    });

    const chooseFulfillment = (key) => {
        setFulfillment(key);
        try { localStorage.setItem("sp_fulfillment", key); } catch {}
    };

    if (isLoading || !product) {
        return (
            <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
                <div className="grid gap-10 lg:grid-cols-2">
                    <div className="aspect-square rounded-3xl skeleton" />
                    <div className="space-y-4">
                        <div className="h-8 w-2/3 rounded skeleton" />
                        <div className="h-24 w-full rounded skeleton" />
                        <div className="h-12 w-full rounded skeleton" />
                    </div>
                </div>
            </div>
        );
    }

    const sizes = config?.sizes || [{ key: "cup12", label: "12oz Cup", modifier: 0 }];
    const priceFor = (s) => (s.absolute != null ? s.absolute : product.price + (s.modifier || 0));
    const size = sizes.find((s) => s.key === sizeKey) || sizes[0];
    const unit = priceFor(size);
    const days = fulfillment === "pickup" ? 0 : fulfillment === "delivery" ? 1 : 2;

    const toggleFav = async () => {
        if (!user) {
            toast("Sign in to save favorites", { description: "Create an account to keep your flavors handy." });
            return;
        }
        try {
            await api.post("/favorites/toggle", { product_id: product.id });
            setFav((f) => !f);
            toast.success(fav ? "Removed from favorites" : "Saved to favorites 💛");
        } catch {
            toast.error("Could not update favorites");
        }
    };

    const add = () => {
        const customization = { base, topping, drizzle, fruit };
        addItem({
            product_id: product.id, slug: product.slug, name: product.name,
            image: product.images?.[0], size_key: sizeKey, size_label: size.label,
            unit_price: unit, quantity: qty, customization,
        });
        const extras = [base, topping, drizzle, fruit].filter((v) => v && v !== "No thanks");
        toast.success(`${product.name} added`, { description: [size.label, ...extras].join(" · ") });
    };

    return (
        <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
            <nav className="mb-6 flex items-center gap-1.5 text-sm text-mocha">
                <Link to="/" className="hover:text-caramel">Home</Link><ChevronRight size={14} />
                <Link to="/shop" className="hover:text-caramel">Shop</Link><ChevronRight size={14} />
                <span className="text-roast">{product.name}</span>
            </nav>

            <div className="grid gap-10 lg:grid-cols-2">
                {/* Gallery */}
                <div>
                    <div
                        className="relative aspect-square overflow-hidden rounded-3xl border border-caramel/10 bg-cream-200 shadow-warm"
                        onMouseMove={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            setZoom({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
                        }}
                        onMouseLeave={() => setZoom(null)}
                        data-testid="product-gallery"
                    >
                        <img
                            src={product.images?.[activeImg]}
                            alt={product.name}
                            className="h-full w-full object-cover transition-transform duration-200"
                            style={zoom ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}
                        />
                        {product.badge && <span className="absolute left-4 top-4 rounded-full bg-banana px-3 py-1 text-xs font-semibold text-roast">{product.badge}</span>}
                    </div>
                    <div className="mt-4 flex gap-3">
                        {product.images?.map((img, i) => (
                            <button
                                key={i}
                                onClick={() => setActiveImg(i)}
                                data-testid={`thumb-${i}`}
                                className={`h-20 w-20 overflow-hidden rounded-2xl border-2 transition-colors ${i === activeImg ? "border-caramel" : "border-transparent opacity-70 hover:opacity-100"}`}
                            >
                                <img src={img} alt="" className="h-full w-full object-cover" />
                            </button>
                        ))}
                    </div>
                </div>

                {/* Info */}
                <div>
                    <div className="flex items-center gap-1 text-gold">
                        <Star size={16} className="fill-gold" />
                        <span className="text-sm font-medium text-roast">{product.rating}</span>
                        <span className="text-sm text-mocha">· {product.review_count} reviews</span>
                    </div>
                    <h1 className="mt-2 font-serif text-4xl font-semibold text-roast">{product.name}</h1>
                    <p className="mt-1 text-caramel">{product.flavor}</p>

                    <AnimatePresence mode="wait">
                        <motion.p key={unit} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 font-serif text-4xl text-caramel" data-testid="product-price">
                            ${unit.toFixed(2)}
                        </motion.p>
                    </AnimatePresence>

                    <p className="mt-4 leading-relaxed text-roast/80">{product.description}</p>

                    {/* Size */}
                    <div className="mt-7">
                        <p className="mb-2 text-sm font-semibold text-roast">Choose size</p>
                        <div className="grid grid-cols-3 gap-2">
                            {sizes.map((s) => (
                                <button
                                    key={s.key}
                                    onClick={() => setSizeKey(s.key)}
                                    data-testid={`size-${s.key}`}
                                    className={`rounded-2xl border px-4 py-3 text-left text-sm transition-all ${sizeKey === s.key ? "border-caramel bg-banana/15 text-roast" : "border-caramel/15 bg-white text-roast/80 hover:border-caramel/40"}`}
                                >
                                    <span className="block font-medium">{s.label}</span>
                                    <span className="text-xs text-mocha">${priceFor(s).toFixed(2)}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Build Your Pudding */}
                    <div className="mt-8 rounded-3xl border border-caramel/15 bg-cream-100/60 p-5" data-testid="build-your-pudding">
                        <h3 className="font-serif text-2xl font-semibold text-roast">Build Your Pudding</h3>
                        <p className="mt-1 text-sm text-mocha">Mix and match to create your perfect banana pudding.</p>

                        <OptionGroup
                            label="Cookie base / crust"
                            options={BASES}
                            value={base}
                            onChange={setBase}
                            testPrefix="base"
                        />
                        <OptionGroup
                            label="Toppings"
                            options={TOPPINGS}
                            value={topping}
                            onChange={setTopping}
                            testPrefix="topping"
                        />
                        <OptionGroup
                            label="Drizzle"
                            options={DRIZZLES}
                            value={drizzle}
                            onChange={setDrizzle}
                            testPrefix="drizzle"
                        />
                        <OptionGroup
                            label="Fresh Fruit"
                            options={FRUITS}
                            value={fruit}
                            onChange={setFruit}
                            testPrefix="fruit"
                        />
                    </div>

                    {/* Fulfillment */}
                    <div className="mt-6">
                        <p className="mb-2 text-sm font-semibold text-roast">Fulfillment</p>
                        <div className="grid grid-cols-3 gap-2">
                            {FULFILL.map((f) => (
                                <button
                                    key={f.key}
                                    onClick={() => chooseFulfillment(f.key)}
                                    data-testid={`fulfillment-${f.key}`}
                                    aria-pressed={fulfillment === f.key}
                                    className={`rounded-2xl border px-3 py-3 text-center transition-all active:scale-95 ${fulfillment === f.key ? "border-caramel bg-banana/20" : "border-caramel/15 bg-white hover:border-caramel/40"}`}
                                >
                                    <f.icon size={20} className="mx-auto text-caramel" />
                                    <p className="mt-1 text-xs font-medium text-roast">{f.label}</p>
                                    <p className="text-[11px] text-mocha">{f.note}</p>
                                </button>
                            ))}
                        </div>
                        <p className="mt-3 flex items-center gap-2 text-sm text-mocha">
                            <Calendar size={15} className="text-caramel" /> Estimated ready: <span className="font-medium text-roast">{fulfillmentDate(days)}</span>
                        </p>
                    </div>

                    {/* Qty + add */}
                    <div className="mt-7 flex items-center gap-4">
                        <div className="flex items-center gap-3 rounded-full border border-caramel/20 bg-white px-2 py-1.5">
                            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-9 w-9 place-items-center rounded-full hover:bg-cream-200" aria-label="Decrease" data-testid="qty-dec"><Minus size={16} /></button>
                            <AnimatePresence mode="wait">
                                <motion.span key={qty} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="w-6 text-center font-medium" data-testid="qty-value">{qty}</motion.span>
                            </AnimatePresence>
                            <button onClick={() => setQty((q) => Math.min(99, q + 1))} className="grid h-9 w-9 place-items-center rounded-full hover:bg-cream-200" aria-label="Increase" data-testid="qty-inc"><Plus size={16} /></button>
                        </div>
                        <button onClick={add} data-testid="add-to-cart-btn" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-caramel px-6 py-3.5 font-semibold text-cream-50 transition-all hover:bg-caramel-dark active:scale-[0.98]">
                            <ShoppingBag size={18} /> Add to Cart · ${(unit * qty).toFixed(2)}
                        </button>
                        <button onClick={toggleFav} data-testid="favorite-btn" className={`grid h-[52px] w-[52px] place-items-center rounded-full border transition-colors ${fav ? "border-caramel bg-banana/20 text-caramel" : "border-caramel/20 bg-white text-roast hover:bg-cream-200"}`} aria-label="Favorite">
                            <Heart size={20} className={fav ? "fill-caramel" : ""} />
                        </button>
                    </div>

                    {/* Details accordion */}
                    <Accordion type="single" collapsible className="mt-8" defaultValue="ingredients">
                        {[
                            { v: "ingredients", t: "Ingredients", c: product.ingredients },
                            { v: "allergens", t: "Allergen Information", c: product.allergens },
                            { v: "serving", t: "Serving Size", c: product.serving },
                            { v: "storage", t: "Storage Instructions", c: product.storage },
                        ].map((row) => (
                            <AccordionItem key={row.v} value={row.v} className="border-caramel/10">
                                <AccordionTrigger className="font-serif text-lg text-roast hover:no-underline">{row.t}</AccordionTrigger>
                                <AccordionContent className="text-mocha">{row.c}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>

            {/* Related */}
            {product.related?.length > 0 && (
                <section className="mt-24">
                    <Reveal><h2 className="font-serif text-3xl font-semibold text-roast">You may also love</h2></Reveal>
                    <Stagger className="mt-20 grid grid-cols-1 gap-x-6 gap-y-20 sm:grid-cols-2 lg:grid-cols-4">
                        {product.related.map((p) => <ProductCard key={p.id} product={p} />)}
                    </Stagger>
                </section>
            )}

            {/* Sticky mobile add */}
            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-caramel/10 bg-cream-50/95 p-3 backdrop-blur-xl lg:hidden">
                <button onClick={add} data-testid="sticky-add-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-caramel py-3.5 font-semibold text-cream-50 active:scale-[0.98]">
                    <ShoppingBag size={18} /> Add to Cart · ${(unit * qty).toFixed(2)}
                </button>
            </div>
        </div>
    );
}
